import React, { RefObject } from 'react';
import isNumber from 'lodash/isNumber';

import { Popover } from '../../../components/Popover/Popover';
import { UsageCondition } from '../../../../../models/Product';

import './UsageConditionPopover.scss';
import { useTranslation } from 'react-i18next';
import FormatCurrency from '../../../utils/formatCurrency';
import { TFunction } from 'i18next';

/**
 * Props for <UsageConditionPopover>.
 */
interface UsageConditionPopoverProps {
    /**
     * The usage condition to display.
     */
    readonly usageCondition: UsageCondition;

    /**
     * Ref to the element the popover is attached to.
     */
    readonly target: RefObject<any>;

    /**
     * Indicates whether the popover is visible or not.
     */
    readonly visible: boolean;

    /**
     * The price for the product
     */
    readonly price: any;
    readonly currency: string;
}

const SavedAmount: React.FunctionComponent<{ price: any; currency: string }> = ({ price, currency }) => {
    const { t } = useTranslation();
    return (
        <div className="discount">
            <del>
                {t('numberFormat.numberWithCurrencySymbolWithoutThousandsSeparator', {
                    val: FormatCurrency.convertIntegerToCurrencyFormat(price.basePrice),
                    currencySymbol: FormatCurrency.mapCurrencyToSymbol(currency)
                })}
            </del>
            <div className="savedAmount">
                {t('reserveReports.reserveReport.viewReport.save', {
                    amount: t('numberFormat.numberWithCurrencySymbolWithoutThousandsSeparator', {
                        val: FormatCurrency.convertIntegerToCurrencyFormat(price.savedValue),
                        currencySymbol: FormatCurrency.mapCurrencyToSymbol(price.currency)
                    }),
                    percentile: Math.round((price.savedPercentage ?? 0) / 100)
                })}
            </div>
        </div>
    );
};

function mapTermLimit(termLimit, t: TFunction) {
    if (!isNumber(termLimit) || termLimit < 0) {
        return t('components.productUsageConditions.noTermLimit');
    }
    if (termLimit >= 365) {
        const termLimitYears = Math.round(termLimit / 365);
        return t('components.productUsageConditions.termLimitYear', { count: termLimitYears });
    }
    if (termLimit < 365 && termLimit > 0) {
        return t('components.productUsageConditions.termLimitDay', { count: termLimit });
    } else {
        return t('components.productUsageConditions.noTermLimit');
    }
}

export function UsageConditionPopover({
    usageCondition,
    target,
    visible,
    price,
    currency
}: UsageConditionPopoverProps) {
    const { t } = useTranslation();
    const usageLimit = isPositiveNumber(usageCondition.usageLimit)
        ? t('components.productUsageConditions.usageLimit', { usageLimit: usageCondition.usageLimit })
        : t('components.productUsageConditions.noLoanLimit');

    return (
        <Popover className="usage-condition-popover" target={target} placement="top" visible={visible}>
            <div className="lending-model">{formatLendingModel(usageCondition.lendingModel, t)}</div>
            <div>{usageLimit}</div>
            <div>{mapTermLimit(usageCondition.termLimit, t)}</div>
            {price.savedPercentage && price.savedPercentage > 0 && <SavedAmount price={price} currency={currency} />}
        </Popover>
    );
}

function formatLendingModel(lendingModel: string | undefined | null, t: TFunction): string | null {
    switch (lendingModel) {
        case 'SIM':
            return t('components.productUsageConditions.simultaneousAccess');
        case 'OCOU':
            return t('components.productUsageConditions.ocou');
        default:
            return null;
    }
}

function isPositiveNumber(num: number | undefined | null): boolean {
    return typeof num === 'number' && num > 0;
}
